import { openingSideOpening2 } from "building-common/openings/openingQuery";
import { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import { tagBuildingFaces } from "building-types/buildingGeomTag/buildingGeomTags";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import {
  TAG_CONDITION_HOLE,
  TAG_POSITION_BACK,
  TAG_POSITION_FRONT,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { Asset } from "building-types/entities/asset";
import type { DocEntityHandle, RoofOpeningHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterialOverride, InteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { OpeningSideHolePlacement } from "building-types/openings/opening";
import type { OpeningSide } from "building-types/openings/openingTypes";
import type { PropAttributeTable } from "building-types/props/propTypes";
import type { RoofWallFaces } from "building-types/roofs/generatedRoofSystemTypes";
import type { GetMergedSettingsForWindow } from "building-types/roofs/roofInputSettings";
import { RoofOpeningJustification, type RoofOpeningPlacement } from "building-types/roofs/roofSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomKind } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type { RoofSlope } from "building-types/settings/roofTypes";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { WindowShapeTypes } from "building-types/settings/windowTypesEnum";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type {
  MergedWindowSettings,
  GeneratedWindowPlacement,
  WindowDescriptor,
  WindowShutters,
  WindowUnit,
} from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNull, assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import { WINDOW_EXTERIOR_FRAME_WIDTH } from "doc/elements/window/constants";
import { assertIsNotFormula } from "doc/formula/formulaUtils";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { faceGroupNew, faceIsDefined, faceNew, faceOrNullMoveAlongNormal, tagFaces } from "geom/brep";
import { buildPerimeterBSP } from "geom/bsp";
import type { ClipFaceContext } from "geom/bspClipping";
import { clipFaces, clippingNodeNew } from "geom/bspClipping";
import { DIST_EPSILON } from "geom/core";
import { geomToFaces } from "geom/geom";
import type GeomTagGroup from "geom/geomTagGroup";
import { planeCopy, planeEval, planeNormal, planeProjectPolyLine3 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2ByTwoPoints, polyLine2Copy, polyLine2EdgeLength, polyLine2SetCW } from "geom/polyLine2";
import { LineAnchorEnum } from "geom/polyLine2Types";
import { polyLine3OnPlane } from "geom/polyLine3";
import { planExtentsFromElevations } from "../../elements/openings/openingUtils";
import { wallOpeningNew } from "../../elements/openings/wallOpeningNew";
import { makeWindowShutters } from "../../elements/shutters";
import { makeWindowSide, windowRectangularOpening } from "../../elements/window/windowElevation";
import { getWindowHeight } from "../../elements/window/windowHeightTools";
import {
  windowCasingClipsFinish,
  windowDescriptorShouldCutWall,
  windowHasJamb,
  windowHasSill,
  windowHasWrapping,
} from "../../elements/window/windowQuery";
import { generatedWindowPlacementDeprecatedNew } from "../../elements/window/windowTools";
import { makeWindowUnit } from "../../elements/window/windowUnit";
import type { WindowShuttersPlacement } from "../../elements/window/windowUtils";
import {
  makeDefaultWindowShuttersPlacement,
  makeDefaultWindowShuttersPlacementLegacy,
} from "../../elements/window/windowUtils";
import { reprojectProfiles } from "../walls/openingProfiles";
import { getBackSide, getHoleFaces, performWallCut } from "../walls/wallCut";
import { getSideSpanAtHeightFromBase } from "./roofSlopes";
import { rebuildRoofWallFaces } from "./roofWallFaces";

interface AddEaveWallWindowOptions {
  roofSystemId: string;
  subRoofId: string;
  baseHeight: number;
  roofOpening: RoofOpeningPlacement;
  span: number;
  maxHeight: number;
  frontFace: Face;
  backPlane: Plane;
  hideInteriorFaces: Face[];
  windows: RWTable<GeneratedWindowPlacement>;
  eaveWindows: RWTable<GeneratedWindowPlacement>;
  windowUnits: RWTable<WindowUnit>;
  windowShutters: RWTable<WindowShutters>;
  wallSettings: WallSettings;
  propCatalog: PropAttributeTable;
  assets: Table<Asset>;
  wainscotingSettings: WainscotingSettings;
  issues: BuildingGenerationIssue[];
  windowCatalog: Table<WindowDescriptor>;
  mirror: boolean;
  getMergedSettingsForWindow: GetMergedSettingsForWindow;
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined;
  /** The hosting roof's component instance, if any, so its windows and shutters select the instance. */
  fallbackInstanceId: string | undefined;
}

export async function addEaveWallWindow({
  baseHeight,
  roofOpening,
  span,
  maxHeight,
  frontFace,
  backPlane,
  hideInteriorFaces,
  windows,
  eaveWindows,
  windowUnits,
  windowShutters,
  wallSettings,
  propCatalog,
  assets,
  wainscotingSettings,
  issues,
  windowCatalog: windowCatalog,
  mirror,
  getMergedSettingsForWindow,
  entityHandleForSelectingReportingUnit,
  fallbackInstanceId,
}: AddEaveWallWindowOptions): Promise<void> {
  const extraWindowBorder = 2.0;
  const windowFrameWidth = WINDOW_EXTERIOR_FRAME_WIDTH + extraWindowBorder;

  const {
    windowType,
    operationType,
    justification,
    offset = 0,
    horizontalOffset = 0,
    materials,
    exteriorCasing,
    interiorCasing,
    mullions,
    shutters,
    awning,
    awningSettings: windowAwningSettings,
    useNewPlacementMethod,
    viewNotes,
    disableAutoDimensions,
    verticalDivisionSpacing,
    side,
    useLegacyAttachmentSettings,
  } = roofOpening;

  assertIsNotFormula(offset);

  const windowPlacementHandle: RoofOpeningHandle = {
    type: "ROOF_OPENING",
    id: roofOpening.id,
  };

  const windowDescriptor = windowCatalog[windowType];

  if (!windowDescriptor) {
    issues.push({
      type: BuildingGenerationIssueType.UNKNOWN_WINDOW_TYPE,
      windowType,
      handle: windowPlacementHandle,
    });

    return;
  }

  const windowTopOffset = useNewPlacementMethod ? 0 : windowFrameWidth;
  const windowTop = getWindowHeight(windowDescriptor) + windowTopOffset;

  let verticalOffset = 0;

  const isOpen = windowDescriptor.open;

  switch (justification) {
    default:
    case RoofOpeningJustification.BASE_UP: {
      verticalOffset = offset;

      if (
        windowTop + verticalOffset + baseHeight + windowFrameWidth > maxHeight &&
        !isOpen &&
        windowDescriptor.shape !== WindowShapeTypes.POLYGONAL
      ) {
        issues.push({
          type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
          handle: windowPlacementHandle,
          params: [
            {
              type: "RANGE",
              issueType: "MAX_EXCEEDED",
              description: `${side?.toLocaleLowerCase()} window top`,
              max: maxHeight,
              value: windowTop + verticalOffset + baseHeight + windowFrameWidth,
            },
          ],
        });
        return;
      }
      break;
    }
    case RoofOpeningJustification.PEAK_DOWN: {
      // ceiling height must be subtracted to coordinate roof max height (relative to global 0'-0" elev) with
      // windowType dims which are relative to each level elev
      const distBelowPeak = maxHeight - baseHeight - windowTop;
      verticalOffset = distBelowPeak - offset;

      if (
        windowTop + verticalOffset + baseHeight + windowFrameWidth > maxHeight &&
        !isOpen &&
        windowDescriptor.shape !== WindowShapeTypes.POLYGONAL
      ) {
        console.assert(false, "Window exceeds gable roof wall boundary");

        issues.push({
          type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
          handle: windowPlacementHandle,
          params: [
            {
              type: "RANGE",
              issueType: "MAX_EXCEEDED",
              description: `${side?.toLocaleLowerCase()} window top`,
              min: 0,
              max: maxHeight - baseHeight,
              value: windowTop + verticalOffset + baseHeight + windowFrameWidth,
            },
          ],
        });

        return;
      }
      break;
    }
  }

  const maxWindowWidth = span - 2.0 * windowFrameWidth;

  const { plane: frontPlane } = frontFace;

  const [frontStartX, frontStartY] = planeEval(frontPlane, 0, 0);
  const [frontEndX, frontEndY] = planeEval(frontPlane, span, 0);

  const [backStartX, backStartY] = planeEval(backPlane, 0, 0);
  const [backEndX, backEndY] = planeEval(backPlane, span, 0);

  const frontEdge = polyLine2ByTwoPoints(frontStartX, frontStartY, frontEndX, frontEndY);
  const backEdge = polyLine2ByTwoPoints(backStartX, backStartY, backEndX, backEndY);
  const edgeLen = polyLine2EdgeLength(frontEdge, 0);

  let frontFaceOrientation: StreetOrientation = StreetOrientation.FRONT;

  if (!useLegacyAttachmentSettings) {
    const [frontFaceNormalX, frontFaceNormalY] = planeNormal(frontFace.plane);

    if (Math.abs(frontFaceNormalY) > Math.abs(frontFaceNormalX) - DIST_EPSILON && frontFaceNormalY < 0) {
      frontFaceOrientation = StreetOrientation.FRONT;
    } else if (Math.abs(frontFaceNormalY) > Math.abs(frontFaceNormalX) - DIST_EPSILON && frontFaceNormalY > 0) {
      frontFaceOrientation = StreetOrientation.BACK;
    } else {
      if (!mirror) {
        frontFaceOrientation = frontFaceNormalX > 0 ? StreetOrientation.RIGHT : StreetOrientation.LEFT;
      } else {
        frontFaceOrientation = frontFaceNormalX > 0 ? StreetOrientation.LEFT : StreetOrientation.RIGHT;
      }
    }
  }

  const param = (mirror ? -1 : 1) * (0.5 - horizontalOffset / edgeLen);

  const window = generatedWindowPlacementDeprecatedNew({
    id: uid(),
    descriptor: windowDescriptor,
    frontRoom: null as unknown as Room, // this preserves behavior without break triggering a bunch of strict nulls elsewhere
    halfEdgeIndex: 0,
    param,
    paramNormalized: true,
    paramAnchor: LineAnchorEnum.START,
    headHeight: getWindowHeight(windowDescriptor),
    opening: undefined,
    verticalOffset,
    materials,
    exteriorCasing,
    interiorCasing,
    mullions,
    shutters,
    operationType: operationType,
    awning,
    awningSettings: windowAwningSettings,
    handle: windowPlacementHandle,
    verticalDivisionSpacing,
    viewNotes: viewNotes,
    disableAutoDimensions,
    streetOrientation: frontFaceOrientation,
  });

  const wallThickness = wallSettings.exteriorWallBaseThickness;

  const leftPlacement: OpeningSideHolePlacement = {
    edge: frontEdge,
    edgeLength: edgeLen,
    edgePlane: frontPlane,
    openingPlane: frontPlane,
    roomKind: RoomKind.EXTERIOR,
  };

  const rightPlacement: OpeningSideHolePlacement = {
    edge: backEdge,
    edgeLength: edgeLen,
    edgePlane: backPlane,
    openingPlane: backPlane,
    roomKind: RoomKind.INTERIOR,
  };

  const mergedWindowSettings = getMergedSettingsForWindow(window, window.descriptor);

  if (
    roofWallTryAddWindowOpening({
      window,
      mergedWindowSettings,
      frontPlacement: rightPlacement,
      backPlacement: leftPlacement,
      wallFaces: undefined,
      wallThickness,
      issues: [],
      isLeftWindow: false,
      mirror,
    })
  ) {
    const windowId = window.id;
    windows[windowId] = window;
    eaveWindows[windowId] = window;

    let shuttersPlacement: WindowShuttersPlacement | null = null;

    if (useLegacyAttachmentSettings) {
      shuttersPlacement = makeDefaultWindowShuttersPlacementLegacy(mergedWindowSettings.shutters, window);
      if (
        windowDescriptor.width + 2 * shuttersPlacement.size > maxWindowWidth ||
        shuttersPlacement.size < DIST_EPSILON
      ) {
        shuttersPlacement = null;
      }
    } else {
      shuttersPlacement = makeDefaultWindowShuttersPlacement(
        mergedWindowSettings.shutters,
        window,
        maxWindowWidth,
        mirror,
      );
    }

    const windowUnit = await makeWindowUnit({
      window,
      issues,
      wallLength: span,
      wallThickness,
      wallSettings,
      propCatalog,
      assets,
      wainscotingSettings,
      datumAlignmentOffset: wallThickness,
      mirror,
      roomOutlines: undefined,
      wallMassSystem: undefined,
      mergedWindowSettings,
      context: {
        isFront: false,
        isLeft: false,
      },
      streetOrientation: frontFaceOrientation,
      useLegacyAttachmentSettings,
      entityHandleForSelectingReportingUnit,
      fallbackInstanceId,
    });

    if (windowUnit) {
      windowUnits[windowId] = windowUnit;

      if (shuttersPlacement) {
        const newShutters = makeWindowShutters({
          handle: windowPlacementHandle,
          window,
          shuttersPlacement,
          roomOutlines: undefined,
          wallMassSystem: undefined,
          issues,
          minZ: Number.NEGATIVE_INFINITY,
          fallbackInstanceId,
        });

        if (newShutters) {
          windowShutters[windowId] = newShutters;
        }
      }

      // generate the temporal face
      if (!isOpen) {
        assertIsNotNullOrUndefined(window.opening);
        const hideInteriorFace = faceNew(
          [polyLine2SetCW(polyLine2Copy(window.opening.back.opening2))],
          planeCopy(frontFace.plane),
        );
        faceOrNullMoveAlongNormal(hideInteriorFace, -wallThickness);
        if (window.descriptor.shape !== WindowShapeTypes.POLYGONAL) {
          hideInteriorFaces.push(hideInteriorFace);
        }
      }
    }
  }
}

interface AddGableWallWindowOptions {
  roofSystemId: string;
  subRoofId: string;
  baseHeight: number;
  roofOpening: RoofOpeningPlacement;
  span: number;
  leftSpan: number;
  rightSpan: number;
  leftSideSlopes: RoofSlope[];
  rightSideSlopes: RoofSlope[];
  leftWallToRoofEdgeDistance: number;
  rightWallToRoofEdgeDistance: number;
  maxHeight: number;
  frontFace: Face | null;
  backFace: Face | null;
  wallFaces: RoofWallFaces;
  hideInteriorFaces: Face[];
  gableWindows: RWTable<GeneratedWindowPlacement>;
  windows: RWTable<GeneratedWindowPlacement>;
  windowUnits: RWTable<WindowUnit>;
  windowShutters: RWTable<WindowShutters>;
  wallSettings: WallSettings;
  propCatalog: PropAttributeTable;
  assets: Table<Asset>;
  wainscotingSettings: WainscotingSettings;
  issues: BuildingGenerationIssue[];
  windowCatalog: Table<WindowDescriptor>;
  mirror: boolean;
  getMergedSettingsForWindow: GetMergedSettingsForWindow;
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined;
  /** The hosting roof's component instance, if any, so its windows and shutters select the instance. */
  fallbackInstanceId: string | undefined;
}

export async function addGableWallWindow({
  baseHeight,
  roofOpening,
  span,
  leftSpan,
  rightSpan,
  leftSideSlopes,
  rightSideSlopes,
  leftWallToRoofEdgeDistance,
  rightWallToRoofEdgeDistance,
  maxHeight,
  frontFace,
  backFace,
  wallFaces,
  hideInteriorFaces,
  gableWindows,
  windows,
  windowUnits,
  windowShutters,
  wallSettings,
  propCatalog,
  assets,
  wainscotingSettings,
  issues,
  windowCatalog,
  mirror,
  getMergedSettingsForWindow,
  entityHandleForSelectingReportingUnit,
  fallbackInstanceId,
}: AddGableWallWindowOptions): Promise<void> {
  const extraWindowBorder = 2.0;
  const windowFrameWidth = WINDOW_EXTERIOR_FRAME_WIDTH + extraWindowBorder;

  const {
    windowType,
    operationType,
    justification,
    offset = 0,
    horizontalOffset = 0,
    materials,
    exteriorCasing,
    interiorCasing,
    mullions,
    shutters,
    awning,
    awningSettings: windowAwningSettings,
    useNewPlacementMethod,
    viewNotes,
    disableAutoDimensions,
    verticalDivisionSpacing,
    side,
    useLegacyAttachmentSettings,
  } = roofOpening;

  assertIsNotFormula(offset);

  const windowPlacementHandle: RoofOpeningHandle = {
    type: "ROOF_OPENING",
    id: roofOpening.id,
  };

  const windowDescriptor = windowCatalog[windowType];

  if (!windowDescriptor) {
    issues.push({
      type: BuildingGenerationIssueType.UNKNOWN_WINDOW_TYPE,
      windowType,
      handle: windowPlacementHandle,
    });

    return;
  }

  const windowTopOffset = useNewPlacementMethod ? 0 : windowFrameWidth;
  const windowTop = getWindowHeight(windowDescriptor) + windowTopOffset;

  let verticalOffset = 0;
  let wallWidthAtWindowTop = span - leftWallToRoofEdgeDistance - rightWallToRoofEdgeDistance;
  let leftWallWidthAtWindowTop = wallWidthAtWindowTop / 2;
  let rightWallWidthAtWindowTop = wallWidthAtWindowTop / 2;

  const isOpen = windowDescriptor.open;

  switch (justification) {
    default:
    case RoofOpeningJustification.BASE_UP: {
      verticalOffset = offset;

      leftWallWidthAtWindowTop = getSideSpanAtHeightFromBase(leftSideSlopes, leftSpan, windowTop + verticalOffset);
      rightWallWidthAtWindowTop = getSideSpanAtHeightFromBase(rightSideSlopes, rightSpan, windowTop + verticalOffset);
      wallWidthAtWindowTop = leftWallWidthAtWindowTop + rightWallWidthAtWindowTop;

      if (
        windowTop + verticalOffset + baseHeight + windowFrameWidth > maxHeight &&
        !isOpen &&
        windowDescriptor.shape !== WindowShapeTypes.POLYGONAL
      ) {
        issues.push({
          type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
          handle: windowPlacementHandle,
          params: [
            {
              type: "RANGE",
              issueType: "MAX_EXCEEDED",
              description: `${side?.toLocaleLowerCase()} window top`,
              max: maxHeight,
              value: windowTop + verticalOffset + baseHeight + windowFrameWidth,
            },
          ],
        });
        return;
      }
      break;
    }
    case RoofOpeningJustification.PEAK_DOWN: {
      //ceiling height must be subtracted to coordinate roof max height (relative to global 0'-0" elev) with
      // windowType dims which are relative to each level elev
      const distBelowPeak = maxHeight - baseHeight - windowTop;
      verticalOffset = distBelowPeak - offset;

      leftWallWidthAtWindowTop = getSideSpanAtHeightFromBase(leftSideSlopes, leftSpan, windowTop + verticalOffset);
      rightWallWidthAtWindowTop = getSideSpanAtHeightFromBase(rightSideSlopes, rightSpan, windowTop + verticalOffset);
      wallWidthAtWindowTop = leftWallWidthAtWindowTop + rightWallWidthAtWindowTop;

      if (
        windowTop + verticalOffset + baseHeight + windowFrameWidth > maxHeight &&
        !isOpen &&
        windowDescriptor.shape !== WindowShapeTypes.POLYGONAL
      ) {
        console.assert(false, "Window exceeds gable roof wall boundary");

        issues.push({
          type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
          handle: windowPlacementHandle,
          params: [
            {
              type: "RANGE",
              issueType: "MAX_EXCEEDED",
              description: `${side?.toLocaleLowerCase()} window top`,
              min: 0,
              max: maxHeight - baseHeight,
              value: windowTop + verticalOffset + baseHeight + windowFrameWidth,
            },
          ],
        });

        return;
      }
      break;
    }
  }

  const maxWindowWidth =
    Math.min(span - leftWallToRoofEdgeDistance - rightWallToRoofEdgeDistance, wallWidthAtWindowTop) -
    2.0 * windowFrameWidth;

  if (windowDescriptor.width > maxWindowWidth && !isOpen && windowDescriptor.shape !== WindowShapeTypes.POLYGONAL) {
    issues.push({
      type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
      handle: windowPlacementHandle,
      params: [
        {
          type: "RANGE",
          issueType: "MAX_EXCEEDED",
          description: "windowDescriptor.width",
          max: maxWindowWidth,
          value: windowDescriptor.width,
        },
      ],
    });
    return;
  }

  const centerOffset = (leftSpan - rightSpan) / 2;
  const finalHorizontalOffset = (mirror ? -1 : 1) * horizontalOffset + centerOffset;
  const halfWidth = windowDescriptor.width / 2;

  if (
    horizontalOffset + halfWidth > rightWallWidthAtWindowTop &&
    !isOpen &&
    windowDescriptor.shape !== WindowShapeTypes.POLYGONAL
  ) {
    issues.push({
      type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
      handle: windowPlacementHandle,
      params: [
        {
          type: "RANGE",
          issueType: "MAX_EXCEEDED",
          description: "horizontalOffset",
          max: rightWallWidthAtWindowTop - halfWidth,
          value: horizontalOffset,
        },
      ],
    });
    return;
  }

  if (
    horizontalOffset - halfWidth < -leftWallWidthAtWindowTop &&
    !isOpen &&
    windowDescriptor.shape !== WindowShapeTypes.POLYGONAL
  ) {
    issues.push({
      type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
      handle: windowPlacementHandle,
      params: [
        {
          type: "RANGE",
          issueType: "MIN_EXCEEDED",
          description: "horizontalOffset",
          min: -(leftWallWidthAtWindowTop - halfWidth),
          value: horizontalOffset,
        },
      ],
    });
    return;
  }

  assertIsNotNull(frontFace);
  assertIsNotNull(backFace);

  const { plane: frontPlane } = frontFace;
  const { plane: backPlane } = backFace;

  const [frontStartX, frontStartY] = planeEval(frontPlane, 0, 0);
  const [frontEndX, frontEndY] = planeEval(frontPlane, span, 0);

  const [backStartX, backStartY] = planeEval(backPlane, 0, 0);
  const [backEndX, backEndY] = planeEval(backPlane, span, 0);

  const frontEdge = polyLine2ByTwoPoints(frontStartX, frontStartY, frontEndX, frontEndY);
  const backEdge = polyLine2ByTwoPoints(backStartX, backStartY, backEndX, backEndY);
  const edgeLen = polyLine2EdgeLength(frontEdge, 0);

  let frontFaceOrientation: StreetOrientation = StreetOrientation.FRONT;

  if (!useLegacyAttachmentSettings) {
    const [frontFaceNormalX, frontFaceNormalY] = planeNormal(frontFace.plane);

    if (Math.abs(frontFaceNormalY) > Math.abs(frontFaceNormalX) - DIST_EPSILON && frontFaceNormalY < 0) {
      frontFaceOrientation = StreetOrientation.FRONT;
    } else if (Math.abs(frontFaceNormalY) > Math.abs(frontFaceNormalX) - DIST_EPSILON && frontFaceNormalY > 0) {
      frontFaceOrientation = StreetOrientation.BACK;
    } else {
      if (!mirror) {
        frontFaceOrientation = frontFaceNormalX > 0 ? StreetOrientation.RIGHT : StreetOrientation.LEFT;
      } else {
        frontFaceOrientation = frontFaceNormalX > 0 ? StreetOrientation.LEFT : StreetOrientation.RIGHT;
      }
    }
  }

  const window = generatedWindowPlacementDeprecatedNew({
    id: uid(),
    descriptor: windowDescriptor,
    frontRoom: null as unknown as Room, // this preserves behavior without break triggering a bunch of strict nulls elsewhere
    halfEdgeIndex: 0,
    param: 0.5 - finalHorizontalOffset / edgeLen,
    paramNormalized: true,
    paramAnchor: LineAnchorEnum.START,
    headHeight: getWindowHeight(windowDescriptor),
    opening: undefined,
    verticalOffset,
    materials,
    exteriorCasing,
    interiorCasing,
    mullions,
    shutters,
    operationType: operationType,
    awning,
    awningSettings: windowAwningSettings,
    handle: windowPlacementHandle,
    verticalDivisionSpacing,
    viewNotes: viewNotes,
    disableAutoDimensions,
    streetOrientation: frontFaceOrientation,
  });

  const wallThickness = wallSettings.exteriorWallBaseThickness;

  const leftPlacement: OpeningSideHolePlacement = {
    edge: frontEdge,
    edgeLength: edgeLen,
    edgePlane: frontPlane,
    openingPlane: frontPlane,
    roomKind: RoomKind.EXTERIOR,
  };

  const rightPlacement: OpeningSideHolePlacement = {
    edge: backEdge,
    edgeLength: edgeLen,
    edgePlane: backPlane,
    openingPlane: backPlane,
    roomKind: RoomKind.INTERIOR,
  };

  const mergedWindowSettings = getMergedSettingsForWindow(window, window.descriptor);

  if (
    roofWallTryAddWindowOpening({
      window,
      mergedWindowSettings,
      frontPlacement: rightPlacement,
      backPlacement: leftPlacement,
      wallFaces,
      wallThickness,
      issues: [],
      isLeftWindow: false,
      mirror,
    })
  ) {
    rebuildRoofWallFaces(wallFaces);

    const windowId = window.id;

    gableWindows[windowId] = window;
    windows[windowId] = window;

    let shuttersPlacement: WindowShuttersPlacement | null = null;

    if (useLegacyAttachmentSettings) {
      shuttersPlacement = makeDefaultWindowShuttersPlacementLegacy(mergedWindowSettings.shutters, window);
      if (
        windowDescriptor.width + 2 * shuttersPlacement.size > maxWindowWidth ||
        shuttersPlacement.size < DIST_EPSILON
      ) {
        shuttersPlacement = null;
      }
    } else {
      shuttersPlacement = makeDefaultWindowShuttersPlacement(
        mergedWindowSettings.shutters,
        window,
        maxWindowWidth,
        mirror,
      );
    }

    let windowUnit = await makeWindowUnit({
      window,
      issues,
      wallLength: wallWidthAtWindowTop,
      wallThickness,
      wallSettings,
      propCatalog,
      assets,
      wainscotingSettings,
      datumAlignmentOffset: wallThickness,
      mirror,
      roomOutlines: undefined,
      wallMassSystem: undefined,
      mergedWindowSettings,
      context: {
        isFront: false,
        isLeft: false,
      },
      streetOrientation: frontFaceOrientation,
      useLegacyAttachmentSettings,
      entityHandleForSelectingReportingUnit,
      fallbackInstanceId,
    });

    if (windowUnit) {
      if (isOpen) {
        windowUnit = trimOpenWindowUnit(windowUnit, frontFace, wallFaces);
      }

      windowUnits[windowId] = windowUnit;

      if (shuttersPlacement) {
        const newShutters = makeWindowShutters({
          handle: windowPlacementHandle,
          window,
          shuttersPlacement,
          roomOutlines: undefined,
          wallMassSystem: undefined,
          issues,
          minZ: Number.NEGATIVE_INFINITY,
          fallbackInstanceId,
        });

        if (newShutters) {
          windowShutters[windowId] = newShutters;
        }
      }

      // generate the temporal face
      if (!isOpen) {
        assertIsNotNullOrUndefined(window.opening);
        const hideInteriorFace = faceNew(
          [polyLine2SetCW(polyLine2Copy(window.opening.back.opening2))],
          planeCopy(frontFace.plane),
        );
        faceOrNullMoveAlongNormal(hideInteriorFace, -wallThickness);
        if (window.descriptor.shape !== WindowShapeTypes.POLYGONAL) {
          hideInteriorFaces.push(hideInteriorFace);
        }
      }
    }
  }
}

interface RoofWallTryAddWindowOpeningOptions {
  window: GeneratedWindowPlacement;
  mergedWindowSettings: MergedWindowSettings;
  frontPlacement: OpeningSideHolePlacement;
  backPlacement: OpeningSideHolePlacement;
  wallThickness: number;
  wallFaces?: WallFaces;
  issues: BuildingGenerationIssue[];
  isLeftWindow: boolean;
  mirror: boolean;
}

const TAG_GABLE_END_TRY_ADD_WINDOW_OPENING: BuildingGeomTag = {
  type: "OPERATION",
  operation: "GABLE_END_TRY_ADD_WINDOW_OPENING",
};

function roofWallTryAddWindowOpening({
  window,
  mergedWindowSettings,
  frontPlacement,
  backPlacement,
  wallThickness,
  wallFaces,
  issues,
  isLeftWindow,
  mirror,
}: RoofWallTryAddWindowOpeningOptions): boolean {
  if (wallFaces) {
    const { leftFaces, rightFaces } = wallFaces;
    if (leftFaces.length === 0 && rightFaces.length === 0) {
      return false;
    }
  }

  const frontEdge = frontPlacement.edge;
  const frontEdgeLen = frontPlacement.edgeLength;
  const frontProfileRectangular = windowRectangularOpening(window, frontEdge, frontEdgeLen, undefined);
  if (!frontProfileRectangular) {
    if (window.handle) {
      issues.push({
        type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
        handle: window.handle,
      });
    }
    return false;
  }

  const backEdge = backPlacement.edge;

  let backProfileRectangular: OpeningSide | null;
  switch (window.type) {
    case "WINDOW": {
      backProfileRectangular = getBackSide({ hole: frontProfileRectangular, frontEdge, backEdge, ignoreLimits: true });
      break;
    }
    case "WINDOW_V2": {
      backProfileRectangular = getBackSide({ hole: frontProfileRectangular, frontEdge, backEdge, ignoreLimits: true });
      break;
    }
  }

  /** It is rare that a WINDOW_V2 would be caught here as the placement algorithm in
   * GenerateOpenings.ts is much more robust than WINDOW. Go there to see the issues
   * for placement.
   */
  if (!backProfileRectangular) {
    if (window.handle) {
      issues.push({
        type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
        handle: window.handle,
      });
    }
    return false;
  }

  // the cut plane for the hole does not always match up with the
  // exterior wall planes, so we need to project it
  const frontHolePlane = frontPlacement.edgePlane;
  const frontPlane = frontPlacement.openingPlane;

  const backHolePlane = backPlacement.edgePlane;
  const backOpeningPlane = backPlacement.openingPlane;

  const frontProjected = reprojectProfiles(frontProfileRectangular, frontPlacement.edgePlane, frontPlane);
  const backProjected = reprojectProfiles(backProfileRectangular, backPlacement.edgePlane, backOpeningPlane);

  const backCasingProfiles = makeWindowSide(
    {
      condition: "EXTERIOR",
      openingPlane: backOpeningPlane,
    },
    backPlacement,
    backProfileRectangular,
    window,
    mergedWindowSettings,
    false,
  );
  const frontCasingProfiles = makeWindowSide(
    {
      condition: "INTERIOR",
      openingPlane: frontPlane,
    },
    frontPlacement,
    frontProfileRectangular,
    window,
    mergedWindowSettings,
    true,
  );

  let frontOutline3 = polyLine3OnPlane(openingSideOpening2(frontProfileRectangular), frontHolePlane);
  const frontOutlineCasing3 = polyLine3OnPlane(openingSideOpening2(frontCasingProfiles), frontHolePlane);
  const frontOutline2OnFrontPlane = planeProjectPolyLine3(frontPlane, frontOutline3);
  const frontOutlineCasing2OnFrontPlane = planeProjectPolyLine3(frontPlane, frontOutlineCasing3);
  frontOutline3 = polyLine3OnPlane(frontOutline2OnFrontPlane, frontPlane);

  let backOutline3 = polyLine3OnPlane(openingSideOpening2(backProfileRectangular), backHolePlane);
  const backOutlineCasing3 = polyLine3OnPlane(openingSideOpening2(backCasingProfiles), backHolePlane);
  const backOutline2OnBackPlane = planeProjectPolyLine3(backOpeningPlane, backOutline3);
  const backCasingProfilesOnBackPlane = planeProjectPolyLine3(backOpeningPlane, backOutlineCasing3);
  backOutline3 = polyLine3OnPlane(backOutline2OnBackPlane, backOpeningPlane);

  const loop = isLeftWindow ? frontOutlineCasing2OnFrontPlane : backCasingProfilesOnBackPlane;
  const plane = isLeftWindow ? frontPlane : backOpeningPlane;

  if (wallFaces && windowDescriptorShouldCutWall(window)) {
    performWallCut(wallFaces, faceNew([loop], plane));
  }

  const frontHoleOnFrontPlane: OpeningSide = {
    ...frontProjected,
    ...frontCasingProfiles,
    context: {
      condition: "INTERIOR",
      openingPlane: frontPlane,
    },
  };

  const backHoleOnBackPlane: OpeningSide = {
    ...backProjected,
    ...backCasingProfiles,
    context: {
      condition: "EXTERIOR",
      openingPlane: backOpeningPlane,
    },
  };

  const hasJamb = windowHasJamb(window);
  const hasWrapping = windowHasWrapping(window);

  const [px, py] = planeNormal(frontHolePlane);

  window.opening = wallOpeningNew({
    handle: { entityType: "WINDOW", id: window.id },
    hasExteriorCasing: window.hasExteriorCasing,
    casingClipsFinish: windowCasingClipsFinish(window, window.exteriorCasing as ExteriorWindowCasingSettings),
    hasJamb,
    hasWrapping,
    hasSill: windowHasSill(window.descriptor),
    front: frontHoleOnFrontPlane,
    back: backHoleOnBackPlane,
    frontPlane: frontPlane,
    backPlane: backOpeningPlane,
    planExtents: planExtentsFromElevations([px, py], frontOutline3, backOutline3),
    planExtentsCasing: planExtentsFromElevations([px, py], frontOutline3, backOutline3),
    mirror,
  });

  if (!hasJamb) {
    const finishWrapFactor = getGableEndHoleSplitFactor(window);

    const frontHoleFaces = tagBuildingFaces(
      getHoleFaces(frontOutline2OnFrontPlane, frontPlane, wallThickness * finishWrapFactor).filter(faceIsDefined),
      [TAG_GABLE_END_TRY_ADD_WINDOW_OPENING, TAG_POSITION_FRONT, TAG_CONDITION_HOLE],
    );
    const backHoleFaces = tagBuildingFaces(
      getHoleFaces(backOutline2OnBackPlane, backOpeningPlane, wallThickness * (1.0 - finishWrapFactor)).filter(
        faceIsDefined,
      ),
      [TAG_GABLE_END_TRY_ADD_WINDOW_OPENING, TAG_POSITION_BACK, TAG_CONDITION_HOLE],
    );

    if (isLeftWindow) {
      wallFaces?.leftHoleFaces.push(...frontHoleFaces);
      wallFaces?.rightHoleFaces.push(...backHoleFaces);
    } else {
      wallFaces?.leftHoleFaces.push(...backHoleFaces);
      wallFaces?.rightHoleFaces.push(...frontHoleFaces);
    }
  }

  return true;
}

function getGableEndHoleSplitFactor(_opening: GeneratedWindowPlacement): number {
  // TODO - wrapping to be controlled by the opening. for now,
  // the default opening finish wrap is half the substrate (core) mass
  return 0.5;
}

const TAG_TRIM_OPEN_WINDOW_UNIT: BuildingGeomTag = {
  type: "OPERATION",
  operation: "TRIM_OPEN_WINDOW_UNIT",
};

function trimOpenWindowUnit(windowUnit: WindowUnit, frontFace: Face, wallFaces: WallFaces): WindowUnit {
  const { plane, loops } = frontFace;

  const bsp = buildPerimeterBSP(loops[0], plane, MULTIGABLE_BSP_EPSILON);
  const options: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(bsp)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const trimGeomTagGroup = (geomTagGroup: GeomTagGroup<InteriorMaterialOverride | ExteriorMaterialOverride>) => {
    geomTagGroup.geom = geomTagGroup.geom.map((x) => {
      return faceGroupNew(
        clipFaces({
          faces: geomToFaces(x),
          options,
        }),
      );
    });
  };

  const {
    interior,
    exterior,
    grids,
    decorations,
    windowPlacement: { id },
  } = windowUnit;

  trimGeomTagGroup(interior);
  trimGeomTagGroup(exterior);
  trimGeomTagGroup(grids);
  trimGeomTagGroup(decorations);

  const tags: BuildingGeomTag[] = [
    {
      type: "PARENT",
      parentType: "WINDOW",
      parentId: id,
    },
    TAG_TRIM_OPEN_WINDOW_UNIT,
  ];

  wallFaces.leftHoleFaces = tagFaces(
    clipFaces({
      faces: wallFaces.leftHoleFaces,
      options,
    }),
    tags,
  );

  wallFaces.rightHoleFaces = tagFaces(
    clipFaces({
      faces: wallFaces.rightHoleFaces,
      options,
    }),
    tags,
  );

  return {
    ...windowUnit,
  };
}
